/**
 * AI Studio — embeds RAGFlow UI inside Converse (Phase 1).
 * Auto-logs into RAGFlow via Converse backend SSO (?auth=) so agents
 * do not see the RAGFlow username/password screen.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

/** Optional deep-links from older Converse routes → RAGFlow paths */
const SECTION_PATHS: Record<string, string> = {
  home: '/',
  'knowledge-bases': '/datasets',
  datasets: '/datasets',
  documents: '/files',
  files: '/files',
  chat: '/chats',
  search: '/searches',
  agents: '/agents',
  memories: '/memories',
  workflows: '/agent-list',
};

/** Docker / private hostnames browsers cannot load (mixed content / DNS). */
function isBrowserUnreachableHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h === 'ragflow-cpu' ||
    h === 'host.docker.internal' ||
    h.endsWith('.docker.internal') ||
    h.endsWith('.localhost')
  );
}

function getRagflowBaseUrl(): string {
  const raw = (process.env.REACT_APP_RAGFLOW_URL || '').trim().replace(/\/$/, '');
  if (raw) {
    try {
      const u = new URL(raw, window.location.origin);
      if (!isBrowserUnreachableHost(u.hostname)) {
        // HTTPS page must not embed http:// (mixed content)
        if (window.location.protocol === 'https:' && u.protocol === 'http:') {
          return `${window.location.origin}/ragflow`;
        }
        return `${u.origin}${u.pathname}`.replace(/\/$/, '');
      }
    } catch {
      /* fall through */
    }
  }
  // Safe default: same-origin path proxy
  return `${window.location.origin}/ragflow`;
}

const AIStudioPage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetPath = useMemo(() => {
    if (!section) return '/';
    return SECTION_PATHS[section] ?? '/';
  }, [section]);

  useEffect(() => {
    let cancelled = false;

    const buildFallback = () => {
      const base = getRagflowBaseUrl();
      const url = targetPath === '/' ? base : `${base}${targetPath}`;
      return `${url}${url.includes('?') ? '&' : '?'}embed=1&cf=3`;
    };

    const load = async () => {
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (!cancelled) setIframeSrc(buildFallback());
          return;
        }

        const resp = await fetch(`${API_BASE_URL}/ragflow/embed-session`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) {
          const detail = await resp.text();
          throw new Error(detail || `embed-session ${resp.status}`);
        }
        const data = await resp.json();
        const auth = data?.auth;
        if (!auth) {
          throw new Error('No auth token returned from embed-session');
        }

        // Always use public base — never docker-internal ui_base.
        const base = getRagflowBaseUrl();
        const path = targetPath === '/' ? '' : targetPath;
        const src =
          `${base}${path}` +
          `?auth=${encodeURIComponent(auth)}&embed=1&cf=3`;

        if (!cancelled) setIframeSrc(src);
      } catch (e: any) {
        console.error('[AI Studio] RAGFlow auto-login failed:', e);
        if (!cancelled) {
          setError(
            'ConverseFlow auto-login failed — opening without SSO. You may need to sign in once.',
          );
          setIframeSrc(buildFallback());
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [targetPath]);

  if (section && !(section in SECTION_PATHS)) {
    return <Navigate to="/dashboard/ai-studio" replace />;
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-background">
      {error && (
        <div className="shrink-0 border-b border-border bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          {error}
        </div>
      )}
      {!iframeSrc ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Signing into ConverseFlow…
        </div>
      ) : (
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          title="ConverseFlow"
          allow="clipboard-read; clipboard-write"
          className="block h-full w-full border-0"
        />
      )}
    </div>
  );
};

export default AIStudioPage;
